/**
 * Editor.js 내부에서 사용하는 DOM 유틸리티를 모방하거나 직접 만듭니다.
 * @param {string} tagName - 만들 HTML 요소의 태그 이름
 * @param {string|string[]} [classNames] - 적용할 CSS 클래스 이름(들)
 * @param {object} [attributes] - 적용할 속성들
 * @returns {HTMLElement}
 */
import {make} from '@/editorjs/utils'; // Editor.js 내부에서 사용하는 DOM 유틸리티를 모방하거나 직접 만듭니다.

/**
 * TextAlignmentTune을 위한 Editor.js Tune 클래스
 * 블록의 텍스트 정렬(left, center, right)을 제어합니다.
 */
export default class TextStyleTune {
    static get isTune() {
        return true;
    }

    constructor({ api, data = {}, block }) {
        this.api = api;
        this.block = block;
        this.data = {
            alignment: data.alignment || '',
        };
        this.wrapper = undefined;
    }

    wrap(blockContent) {
        this.wrapper = make('div');
        this.applyAlignment();
        this.wrapper.appendChild(blockContent);
        return this.wrapper;
    }

    /**
     * 튠 메뉴에 표시될 커스텀 HTML UI를 렌더링합니다.
     * 한 줄에 여러 버튼을 표시하기 위해 `type: 'html'`을 사용합니다.
     * @returns {Array<object>}
     */
    render() {
        return [
            {
                type: 'html',
                element: this.createAlignmentControls(),
            },
            {
                type: "separator"
            }
        ];
    }

    /**
     * 정렬 버튼(left, center, right)들을 담고 있는 컨테이너 div를 생성합니다.
     * @returns {HTMLElement} - 버튼들을 포함하는 컨테이너 요소
     */
    createAlignmentControls() {
        // 버튼들을 감싸는 flex 컨테이너를 생성합니다.
        const container = make('div', 'cdx-tune-alignment-row');

        // 각 정렬 옵션에 대한 설정
        const actions = [
            {
                name: 'left',
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="17" y1="14" x2="3" y2="14"/><line x1="21" y1="18" x2="3" y2="18"/></svg>`,
                title: '왼쪽 정렬',
            },
            {
                name: 'center',
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="17" y1="10" x2="7" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="7" y2="18"/></svg>`,
                title: '가운데 정렬',
            },
            {
                name: 'right',
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" y1="10" x2="7" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="7" y2="14"/><line x1="21" y1="18" x2="3" y2="18"/></svg>`,
                title: '오른쪽 정렬',
            },
            {
                name: 'justify',
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="21" y1="18" x2="3" y2="18"/></svg>`,
                title: '양쪽 정렬',
            },
        ];

        // 각 action에 대해 버튼을 생성하고 컨테이너에 추가합니다.
        actions.forEach(action => {
            const button = make('button', 'cdx-tune-alignment-button', {
                type: 'button',
                innerHTML: action.icon,
                title: action.title,
            });

            // 현재 활성화된 상태이면 'active' 클래스를 추가합니다.
            if (this.data.alignment === action.name) {
                button.classList.add('cdx-tune-alignment-button--active');
            }

            // 버튼 클릭 이벤트 리스너를 추가합니다.
            button.addEventListener('click', () => {
                // 토글 로직: 이미 활성화된 버튼을 클릭하면 정렬 해제
                const newAlignment = (this.data.alignment === action.name) ? '' : action.name;
                this.data.alignment = newAlignment;

                // UI 업데이트: 모든 버튼에서 active 클래스를 제거하고,
                // 새로 선택된 버튼에만 active 클래스를 추가합니다.
                container.querySelectorAll('.cdx-tune-alignment-button').forEach(btn => {
                    btn.classList.remove('cdx-tune-alignment-button--active');
                });
                if (newAlignment) {
                    button.classList.add('cdx-tune-alignment-button--active');
                }

                // 변경된 스타일을 블록에 적용하고 저장합니다.
                this.applyAlignment();
                this.block.save();
            });

            container.appendChild(button);
        });

        return container;
    }

    /**
     * this.data에 저장된 정렬 값을 this.wrapper 요소의 textAlign 스타일에 적용합니다.
     */
    applyAlignment() {
        if (this.wrapper) {
            this.wrapper.style.textAlign = this.data.alignment;
        }
    }

    /**
     * editor.save()가 호출될 때 실행됩니다.
     */
    save() {
        if (!this.data.alignment) {
            return {};
        }
        return this.data;
    }
}